import { useCallback, useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, X } from "lucide-react";

type Props = {
  file: File | null;
  onFile: (file: File | null) => void;
  disabled?: boolean;
};

const MAX_SIZE = 50 * 1024 * 1024;
const ACCEPTED = [".csv", ".xlsx", ".xls"];

function isAccepted(name: string): boolean {
  const low = name.toLowerCase();
  return ACCEPTED.some((ext) => low.endsWith(ext));
}

export function FileDropzone({ file, onFile, disabled }: Props) {
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const accept = useCallback(
    (f: File | null) => {
      setErr(null);
      if (!f) {
        onFile(null);
        return;
      }
      if (!isAccepted(f.name)) {
        setErr("Formato invalido. Use CSV, XLSX ou XLS.");
        return;
      }
      if (f.size > MAX_SIZE) {
        setErr("Arquivo excede 50MB.");
        return;
      }
      onFile(f);
    },
    [onFile],
  );

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (disabled) return;
          const f = e.dataTransfer.files?.[0] ?? null;
          accept(f);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          drag
            ? "border-brand-400 bg-brand-50"
            : "border-zinc-300 bg-zinc-50/60 hover:border-brand-300 hover:bg-brand-50/40"
        } ${disabled ? "pointer-events-none opacity-60" : ""}`}
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white text-brand-500 shadow-sm">
          <UploadCloud className="h-6 w-6" />
        </div>
        <p className="mt-3 text-sm font-semibold text-zinc-800">
          {drag ? "Solte o arquivo aqui" : "Arraste um arquivo ou clique pra selecionar"}
        </p>
        <p className="mt-1 text-xs text-zinc-500">CSV, XLSX ou XLS — ate 50MB</p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(",")}
          className="hidden"
          onChange={(e) => accept(e.target.files?.[0] ?? null)}
        />
      </div>

      {err && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {err}
        </div>
      )}

      {file && !err && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 shrink-0 text-emerald-600" />
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-zinc-900">{file.name}</p>
              <p className="text-[10px] text-zinc-500">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              accept(null);
            }}
            className="rounded p-1 text-zinc-500 hover:bg-white hover:text-zinc-900"
            aria-label="Remover arquivo"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
